import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import WebApp from '@twa-dev/sdk'
import { apiClient } from '../api/client'
import { getTelegramUserId } from '../utils/telegram'
import { getAnalyticsContext, trackBusinessEvent } from '../utils/analytics'
import { useLanguage } from '../hooks/useLanguage'

interface RechargeRecord {
  id: string
  amount: number
  credits?: number
  cny_amount?: string
  usdt_amount?: string
  created_at: string
  paid_at?: string
  status: string
  source: string
  type?: string
}

interface BalanceResponse {
  balance: number
  spent: number
  currency: string
  recharge_history: RechargeRecord[]
  spend_history: unknown[]
}

interface DepositConfigResponse {
  network: string
  token: string
  token_contract: string
  deposit_address: string
  rate: string
}

interface DepositVerifyResponse {
  success: boolean
  error?: string
  credits?: number
  wallet?: BalanceResponse
}

export function BalancePage() {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const [wallet, setWallet] = useState<BalanceResponse | null>(null)
  const [depositConfig, setDepositConfig] = useState<DepositConfigResponse | null>(null)
  const [txHash, setTxHash] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isVerifying, setIsVerifying] = useState(false)

  const loadBalance = async () => {
    setIsLoading(true)
    try {
      const data = await apiClient.get<BalanceResponse>('/api/user/balance', {
        chat_id: getTelegramUserId(),
      })
      setWallet(data)

      const config = await apiClient.get<DepositConfigResponse>('/api/user/deposit/config')
      setDepositConfig(config)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadBalance().catch((error) => {
      console.error('Failed to load balance:', error)
      setIsLoading(false)
    })
  }, [])

  const handleVerifyDeposit = async () => {
    const hash = txHash.trim()
    if (!hash) {
      WebApp.showAlert(t('请输入 BSC 交易哈希', 'Enter the BSC transaction hash'))
      return
    }

    setIsVerifying(true)
    trackBusinessEvent('deposit_verify_click', {
      tx_hash: hash,
      tx_hash_length: hash.length,
      network: 'BSC',
      token: 'USDT',
    })

    try {
      const result = await apiClient.post<DepositVerifyResponse>('/api/user/deposit/verify', {
        chat_id: getTelegramUserId(),
        tx_hash: hash,
        analytics_context: getAnalyticsContext(),
      })

      if (result.success && result.wallet) {
        setWallet(result.wallet)
        setTxHash('')
        WebApp.HapticFeedback.notificationOccurred('success')
        WebApp.showAlert(t(
          `充值成功，获得 ${result.credits || 0} credits`,
          `Recharge successful. You received ${result.credits || 0} credits.`
        ))
      } else {
        WebApp.HapticFeedback.notificationOccurred('error')
        WebApp.showAlert(result.error || t('充值验证失败', 'Recharge verification failed'))
      }
    } catch (error) {
      console.error('Deposit verify failed:', error)
      WebApp.showAlert(error instanceof Error ? error.message : t('充值验证失败', 'Recharge verification failed'))
    } finally {
      setIsVerifying(false)
    }
  }

  const handleCopyDepositAddress = async () => {
    const address = depositConfig?.deposit_address || '0xAe85cCE8a2ff7CBC5c55Ae6Fe297f5BE2ABFe21C'

    try {
      await navigator.clipboard.writeText(address)
      WebApp.HapticFeedback.notificationOccurred('success')
      WebApp.showAlert(t('收款地址已复制', 'Deposit address copied'))
      trackBusinessEvent('deposit_address_copy', {
        network: 'BSC',
        token: 'USDT',
      })
    } catch (error) {
      console.error('Copy deposit address failed:', error)
      WebApp.showAlert(t('复制失败，请手动复制地址', 'Copy failed. Please copy the address manually.'))
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="sticky top-0 z-20 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200"
            aria-label={t('返回', 'Back')}
          >
            ‹
          </button>
          <div>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">{t('我的余额', 'Balance')}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('充值和消费记录', 'Recharge and spending records')}</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 p-4">
        <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">{t('当前余额', 'Current Balance')}</div>
          <div className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
            {isLoading ? '--' : wallet?.balance ?? 0}
            <span className="ml-1 text-sm font-medium text-gray-500 dark:text-gray-400">
              {wallet?.currency || 'credits'}
            </span>
          </div>
        </section>

        <button
          type="button"
          onClick={() => navigate('/spending')}
          className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left dark:border-gray-700 dark:bg-gray-800"
        >
          <div className="text-xs text-gray-500 dark:text-gray-400">{t('已经消费', 'Spent')}</div>
          <div className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
            {isLoading ? '--' : wallet?.spent ?? 0}
            <span className="ml-1 text-sm font-medium text-gray-500 dark:text-gray-400">
              {wallet?.currency || 'credits'}
            </span>
          </div>
        </button>

        <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
            {t('USDT 充值', 'USDT Recharge')}
          </h2>
          <div className="space-y-3 text-sm">
              <div className="grid grid-cols-[88px_1fr] gap-2">
                <span className="text-gray-500 dark:text-gray-400">{t('网络', 'Network')}</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {depositConfig?.network || 'BSC / BNB Smart Chain'}
                </span>
                <span className="text-gray-500 dark:text-gray-400">{t('币种', 'Token')}</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {depositConfig?.token || 'USDT BEP20'}
                </span>
                <span className="text-gray-500 dark:text-gray-400">{t('兑换', 'Rate')}</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {depositConfig?.rate || '1 USDT = 100 credits'}
                </span>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400">{t('收款地址', 'Deposit Address')}</div>
                  <button
                    type="button"
                    onClick={handleCopyDepositAddress}
                    className="rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-600 dark:bg-blue-900/30 dark:text-blue-300"
                  >
                    {t('复制', 'Copy')}
                  </button>
                </div>
                <div className="break-all rounded-md bg-gray-100 px-3 py-2 font-mono text-xs text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                  {depositConfig?.deposit_address || '0xAe85cCE8a2ff7CBC5c55Ae6Fe297f5BE2ABFe21C'}
                </div>
              </div>

              <input
                value={txHash}
                onChange={(event) => setTxHash(event.target.value)}
                placeholder={t('请输入 BSC 交易哈希', 'Enter the BSC transaction hash')}
                className="w-full rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-900 outline-none dark:bg-gray-900 dark:text-white"
              />

              <button
                type="button"
                disabled={isVerifying}
                onClick={handleVerifyDeposit}
                className="w-full rounded-md bg-blue-500 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {isVerifying ? t('验证中...', 'Verifying...') : t('验证充值', 'Verify Recharge')}
              </button>

              <p className="text-xs leading-relaxed text-red-500">
                {t(
                  '仅支持 BSC / BNB Smart Chain 的 USDT BEP20 转账，其他网络或币种无法自动入账。',
                  'Only USDT BEP20 transfers on BSC / BNB Smart Chain are supported. Other networks or tokens cannot be credited automatically.'
                )}
              </p>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">{t('历史充值记录', 'Recharge History')}</h2>

          {!isLoading && (!wallet?.recharge_history || wallet.recharge_history.length === 0) && (
            <div className="text-sm text-gray-500 dark:text-gray-400">{t('暂无充值记录', 'No recharge records')}</div>
          )}

          <div className="space-y-2">
            {wallet?.recharge_history.map((record) => (
              <div
                key={record.id}
                className="flex items-center justify-between gap-3 border-t border-gray-100 py-2 first:border-t-0 dark:border-gray-700"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    +{record.credits || record.amount} credits
                  </div>
                  <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                    {record.source}
                    {record.cny_amount ? ` · ¥${record.cny_amount}` : ''}
                    {record.usdt_amount ? ` · ${record.usdt_amount} USDT` : ''}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(record.paid_at || record.created_at).toLocaleString()}
                  </div>
                </div>
                <span className="shrink-0 text-xs text-green-600 dark:text-green-300">{record.status}</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  )
}
